import graphql from 'babel-plugin-relay/macro';
import {FormikHelpers} from 'formik';
import React from 'react';
import {useLazyLoadQuery, useMutation} from 'react-relay/hooks';
import {useHistory, useParams} from 'react-router';
import {showEventNotification} from 'src/utils/notifications';
import {withSuspense} from 'src/utils/withSuspense';

import Form from '../components/Form';
import {FormValues} from '../types';
import {updatePageHydrantFamilyQuery} from './__generated__/updatePageHydrantFamilyQuery.graphql';
import {updatePageUpdateHydrantFamilyMutation} from './__generated__/updatePageUpdateHydrantFamilyMutation.graphql';

function UpdatePage() {
  const {id} = useParams<{id: string}>();
  const history = useHistory();

  const [commit, isInFlight] = useMutation<updatePageUpdateHydrantFamilyMutation>(
    updateHydrantFamilyMutation,
  );

  const data = useLazyLoadQuery<updatePageHydrantFamilyQuery>(query, {
    hydrantFamilyId: id,
  });

  if (!data.hydrantFamily) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <h4 className="text-lg text-gray-700">The item you wish to update could not be found.</h4>
      </div>
    );
  }

  const initialValues = handleInitialValues();

  return (
    <Form
      title="Update Hydrant Type"
      button="Update Hydrant Type"
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isSubmitting={isInFlight}
    />
  );

  function handleInitialValues() {
    if (!data || !data.hydrantFamily) {
      return;
    }

    const {hydrantFamily} = data;

    return {
      name: hydrantFamily.name,
      manufacturerId: hydrantFamily.manufacturer.id,
    };
  }

  function handleSubmit(values: FormValues, formikHelpers: FormikHelpers<FormValues>) {
    commit({
      variables: {
        input: {
          hydrantFamilyId: id,
          updateHydrantFamilyData: values,
        },
      },
      onCompleted: payload => {
        if (
          payload.updateHydrantFamily?.errors[0] &&
          payload.updateHydrantFamily?.errors[0].fields
        ) {
          payload.updateHydrantFamily?.errors[0].fields.forEach(fieldError => {
            formikHelpers.setFieldError(fieldError.fieldName, fieldError.message);
          });
        }

        const entry = payload.updateHydrantFamily?.hydrantFamily;

        if (entry) {
          showEventNotification('updated', 'Type', entry.name);

          if (history.location.search.includes('newTab')) {
            window.close();
          }

          return history.push('/admin/hydrant_types');
        }
      },
    });
  }
}

const query = graphql`
  query updatePageHydrantFamilyQuery($hydrantFamilyId: ID!) {
    hydrantFamily(hydrantFamilyId: $hydrantFamilyId) {
      id
      createdAt
      name
      manufacturer {
        id
        name
      }
    }
  }
`;

const updateHydrantFamilyMutation = graphql`
  mutation updatePageUpdateHydrantFamilyMutation($input: UpdateHydrantFamilyInput!) {
    updateHydrantFamily(input: $input) {
      hydrantFamily {
        id
        createdAt
        name
        manufacturer {
          id
          name
        }
      }
      errors {
        code
        fields {
          fieldName
          message
        }
      }
    }
  }
`;

export default withSuspense(UpdatePage);
